import React, { useMemo, useState, type FC, useRef } from 'react';
import type { HeroDetailedProps } from './types';

import styled, { css } from 'styled-components';
import {
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  ApiService,
  baseMediaEndpoint,
  generateAccessKey
} from 'shared/helpers/index';
import { useSales, useWebsiteEditor, useAlert, useSublineColor, useBook } from 'shared/hooks/index';
import { useMutation } from 'react-query';

import { Button, Wrapper, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { ConditionalImage } from '../ConditionalImage';
import SvgIcons from 'shared/components/Common/IconsHeroDetailed';

const HeroDetailed = styled.div<{ isEditable?: boolean }>`
  position: relative;
  padding: 60px 0 60px;

  @media screen and (max-width: 991px) {
    padding-bottom: 30px;
  }

  @media screen and (max-width: 575px) {
    padding: 50px 0 0;
  }
`;

const Banner = styled.div`
  max-width: 1820px;
  position: relative;
  margin: 0 auto;
  min-height: 770px;
  display: flex;
  align-items: center;

  @media screen and (max-width: 1460px) {
    width: 90%;
  }

  @media screen and (max-width: 1160px) {
    width: 94%;
  }

  @media screen and (max-width: 1030px) {
    padding-right: 40px;
    padding-left: 40px;
    min-height: 652px;
  }

  @media screen and (max-width: 575px) {
    align-items: center;
    justify-content: center;
    padding: 60px 40px 30px 40px;
  }
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  justify-content: space-between;
  max-width: 100%;
  gap: 65px;
  align-items: center;

  @media screen and (max-width: 991px) {
    gap: 46px;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column-reverse;
    padding-bottom: 90px;
  }

  @media screen and (max-width: 575px) {
    padding-bottom: 50px;
  }
`;

const TitleWrapper = styled.div`
  position: relative;
  z-index: 27;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 9% 0;
  width: 52%;

  @media screen and (max-width: 768px) {
    width: 100%;
    padding: 0;
  }

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.25em;
  color: #fff;
  margin-top: -20px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  @media screen and (max-width: 768px) {
    margin-top: 0;
    text-align: center;
  }
`;

const ShapeTitle = styled.div`
  width: 94px;
  height: 8px;
  margin: 18px 0 28px;

  @media screen and (max-width: 768px) {
    margin: 18px auto 28px;
    width: 84px;
    height: 7px;
  }
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  line-height: 1.35em;
  margin-bottom: 56px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  @media screen and (max-width: 768px) {
    margin-top: 0;
    text-align: center;
    margin-bottom: 38px;
  }
`;

const BtnInner = styled.div`
  display: flex;
  justify-content: flex-start;

  @media screen and (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const buttonStyles = css`
  max-width: 425px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding: 19px 32px;
      margin: 0 auto;
      display: block;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const StyledButton = styled(Button)`
  ${buttonStyles}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  height: 100% !important;
  width: 100% !important;
  border-radius: 32px;

  @media screen and (max-width: 575px) {
    border-radius: 16px;
  }
`;

const BookMediaImg = styled(ImageComp)<{ objposition?: string }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ objposition }) => objposition || 'center center'};
`;

const BookMediaImgMockup = styled(ImageComp)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;

  @media screen and (max-width: 768px) {
    width: 100%;
    height: 100%;
    position: initial !important;
  }
`;

const ImgInner = styled.div`
  width: 47%;
  position: relative;
  z-index: 36;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 768px) {
    width: 58%;
  }

  @media screen and (max-width: 490px) {
    width: 92%;
  }
`;
const imageStylesMockup = css`
  position: relative;
`;

const ConditionalImageStyled = styled(ConditionalImage)<{ width: number; height: number }>`
  max-width: 100%;
  max-height: 920px;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: auto !important;
  aspect-ratio: ${({ width, height }) => (width && height ? `${width} / ${height}` : '536 / 720')};
  object-fit: cover;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 991px) {
    width: 100% !important;
    height: auto !important;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  left: 0;
  min-height: 648px;
  width: 100%;
  height: 90%;
  right: 0;
  margin: 0 auto;
  z-index: 1;

  @media screen and (max-width: 768px) {
    bottom: 0;
    height: 96%;
  }

  @media screen and (max-width: 575px) {
    bottom: 0;
    height: 100%;
  }
`;

const Overlay = styled.div<{ color1?: string; color2?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
`;

const OverlayBlur = styled(Overlay)`
  z-index: 1;
  background: none;
`;

const Benefits = styled.div`
  max-width: 1820px;
  left: 0;
  right: 0;
  margin: 0 auto;
  position: relative;
  z-index: 0;
  border-radius: 0 0 32px 32px;

  @media screen and (max-width: 1460px) {
    width: 90%;
  }

  @media screen and (max-width: 1030px) {
    padding-right: 40px;
    padding-left: 40px;
  }

  @media screen and (max-width: 1160px) {
    width: 94%;
    padding: 0;
  }

  @media screen and (max-width: 768px) {
    margin-top: 0px;
    padding-bottom: 0;
  }
`;

const StyledWrapperBenefits = styled(StyledWrapper)`
  @media screen and (max-width: 768px) {
    padding-bottom: 0;
  }
`;

const BenefitsInner = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  z-index: 24;
  padding: 38px 0 0;

  @media screen and (max-width: 768px) {
    padding: 42px 0 0px;
    padding-bottom: 0;
  }

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
    bottom: auto;
    padding-bottom: 80px;
    padding-top: 50px;
    `}
`;

const BenefitBorder = styled.div`
  width: 1px;
  opacity: 0.12;

  &:last-child {
    display: none;
  }

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    height: 1px;
    `}
`;

const BenefitItem = styled.div`
  width: 28.5%;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: column;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: row;
    width: 100%;
    padding: 18px 0px;
    border-radius: 12px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
    `}
`;

const StyledSvgIcons = styled(SvgIcons)`
  width: 62px;
  height: 62px;
  margin-right: 22px;
  border-radius: 50%;
  margin-bottom: 22px;

  @media screen and (max-width: 768px) {
    margin-bottom: 15px;
  }
`;

const BenefitTxtInner = styled.div`
  width: 100%;
  text-align: center;

  @media screen and (max-width: 575px) {
    text-align: left;
  }
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 7px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

export const V9: FC<HeroDetailedProps> = (props) => {
  const {
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    backgroundColor = '#1CAEB2',
    customBackgroundColor,
    textColor,
    benefits = [],
    meta,
    hidden,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { bookMedia } = useBook();

  const { addAlert } = useAlert();
  const {
    purchaseElementId,
    updateImageMeta,
    type,
    salesPageId,
    isDashboard,
    renderSales,
    metaTags
  } = useSales();
  const [sublineColor, setSublineColor] = useState<string>('#fff');
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const [isLoading, setIsLoading] = useState([false, false]);
  const topRef = useRef<HTMLDivElement>(null);

  useSublineColor(topRef, setSublineColor, renderSales);

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const { mutateAsync: uploadeImage } = useMutation({
    mutationFn: (payload: FormData) =>
      ApiService.post(`/sales/sales-page/${salesPageId}/media/`, payload),
    onError: () => {
      addAlert('Please upload png, jpg and jpeg image only', 'warning');
    }
  });

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('ht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('hc', order), [order]);
  const buttonAccessKey = useMemo(() => generateAccessKey('hb', order), [order]);
  const backgroundAccessKey = useMemo(() => generateAccessKey('hbg', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-hero-${meta['accessorKey'] || 'hero'}`, [meta]);

  const buttonType = useMemo(() => {
    return meta['url']
      ? 'external link'
      : type === 'delivery'
        ? 'delivery action'
        : 'purchase button';
  }, [meta, type]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Background image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File, metaName: string) => {
    if (!meta['accessorKey'] || !editable) return;

    try {
      setIsLoading([metaName === 'image_1', metaName === 'image_2']);
      const formData = new FormData();
      formData.append('type', meta['accessorKey']);
      formData.append('file', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        // img.onload = () => {
        //   const width = img.width;
        //   const height = img.height;
        //   setResize({ width, height });
        //   updateImageMeta(meta['accessorKey'], width, 'width');
        //   updateImageMeta(meta['accessorKey'], height, 'height');
        // };
        img.src = e.target?.result as string;
      };

      reader.readAsDataURL(file);

      const { data } = await uploadeImage(formData);
      updateImageMeta(meta['accessorKey'], data.file, metaName);
      setIsLoading([false, false]);
    } catch (error) {
      handleUploadError(error);
      addAlert('Something went wrong', 'error');
    }
  };

  const bookMockup = useMemo(() => {
    if (meta['image_1'] && !meta['image_placeholder']) {
      return meta['image_1'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const bgImage2 = useMemo(() => {
    if (!meta['image_2'] && metaTags?.['amazon_url']) {
      const media = bookMedia?.find((media) => media.name === 'hero_mockup');

      if (media?.url) {
        updateImageMeta(meta['accessorKey'], media.url, 'image_2');
      }
    }

    if (meta['image_2']) {
      if (baseMediaEndpoint?.includes('localhost'))
        return `${baseMediaEndpoint}${meta['image'] || meta['image_2']}`;
      return meta['image_2'];
    }

    return 'https://pagewheel-public.s3.us-east-1.amazonaws.com/imgs/bgHero9_1.jpg';
  }, [meta, bookMedia, metaTags, updateImageMeta]);

  const opacityBg = useMemo(
    () => (meta['background_opacity'] ? `${meta['background_opacity']}%` : '80%'),
    [meta]
  );

  const blurBg = useMemo(
    () => (meta['overlay_blur'] ? `blur(${meta['overlay_blur']}px)` : 'blur(3px)'),
    [meta]
  );

  const objPosition = useMemo(() => meta['img_obj_position'] || 'center center', [meta]);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor || backgroundColor || '#1CAEB2';
  }, [backgroundColor, customBackgroundColor]);

  return (
    <HeroDetailed
      isEditable={isDashboard}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: sublineColor
      }}
      aria-label="Full-screen hero section with background image"
      role="region"
      ref={topRef}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero content</a>
      </span>
      <div>
        <Banner>
          <StyledWrapper id={skipLinkId}>
            <TitleWrapper id="HeadlineSection">
              <Title
                id={title.id}
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichText(title.val, isDashboard)
                }}
                style={{
                  color: filterBackgroundColor(customBackgroundColor || '#000', textColor)
                }}
                isEmpty={isTitleEmpty}
                {...(isDashboard
                  ? {
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        try {
                          updateActiveElement(e);
                        } catch (error) {
                          handleEditError(error);
                        }
                      },
                      onBlur: resetActiveElement,
                      'data-accessor-parent': meta['accessorKey'],
                      'data-accessorkey': 'title',
                      'data-placeholder': '+',
                      editable: true
                    }
                  : {
                      'aria-label':
                        isTitleEmpty || !title.val?.trim() ? 'Hero section main title' : undefined,
                      accessKey: titleAccessKey,
                      title: `Press Alt+${titleAccessKey} to focus this hero title`,
                      tabIndex: 0
                    })}
              />
              <ShapeTitle style={{ background: button?.color }} />
              <Description
                id={content.id}
                dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
                style={{
                  color: filterBackgroundColor(customBackgroundColor || '#000', textColor)
                }}
                isEmpty={isContentEmpty}
                {...(isDashboard
                  ? {
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        try {
                          updateActiveElement(e);
                        } catch (error) {
                          handleEditError(error);
                        }
                      },
                      onBlur: resetActiveElement,
                      'data-accessor-parent': meta['accessorKey'],
                      'data-accessorkey': 'content',
                      'data-placeholder': '+',
                      editable: true
                    }
                  : {
                      role: 'textbox',
                      'aria-multiline': 'true',
                      'aria-label':
                        isContentEmpty || !content.val?.trim()
                          ? 'Hero section description'
                          : undefined,
                      'aria-describedby': title.id,
                      accessKey: contentAccessKey,
                      title: `Press Alt+${contentAccessKey} to focus this hero description`,
                      tabIndex: 0
                    })}
              />
              <BtnInner role="group" aria-label={`Hero action area - ${buttonType}`}>
                {meta['url'] || type === 'delivery' ? (
                  <DownloadStyled
                    textColor={textColor}
                    button={button}
                    meta={meta}
                    outerOnClick={() => {
                      if (editable) {
                        try {
                          // empty needs only for error
                        } catch (error) {
                          handleModalError(error);
                        }
                      }
                    }}
                    {...(!isDashboard && {
                      accessKey: buttonAccessKey,
                      title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                      'aria-describedby': `${title.id} ${content.id}`
                    })}
                  />
                ) : (
                  <StyledButton
                    to={purchaseElementId}
                    button={button}
                    meta={meta}
                    outerOnClick={() => {
                      if (editable) {
                        try {
                          // empty needs only for error
                        } catch (error) {
                          handleModalError(error);
                        }
                      }
                    }}
                    {...(!isDashboard && {
                      accessKey: buttonAccessKey,
                      title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                      'aria-describedby': `${title.id} ${content.id}`
                    })}
                  />
                )}
              </BtnInner>
            </TitleWrapper>
            <ImgInner>
              <ConditionalImageStyled
                isSalePage
                css={imageStylesMockup as never}
                width={resize.width}
                height={resize.height}
                disabled={!editable}
                resizeSelector={editable}
                handleResize={({ width, height }) => {
                  setResize({ width, height });
                  updateImageMeta(meta['accessorKey'], width, 'width');
                  updateImageMeta(meta['accessorKey'], height, 'height');
                }}
                onUpload={async (file: File) => {
                  try {
                    await handleUpload(file, 'image_1');
                  } catch (error) {
                    handleUploadError(error);
                  }
                }}
                loading={isLoading[0]}
                enableLinks
                alreadyUploaded={Boolean(bookMockup)}
                imageFile={bookMockup}
                initialPosition={{
                  x: meta['x_1'] || 0,
                  y: meta['y_1'] || 0,
                  width: meta['d_width_1'],
                  height: meta['d_height_1']
                }}
                meta={meta}
                positionEnabled
                handlePosition={(width: number, height: number, x: number, y: number) => {
                  updateImageMeta(meta['accessorKey'], x, 'x_1');
                  updateImageMeta(meta['accessorKey'], y, 'y_1');
                  updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                  updateImageMeta(meta['accessorKey'], height, 'd_height_1');
                }}
                imageAspectRatio={{
                  ratio: '3x4',
                  pixels:
                    resize.width && resize.height ? `${resize.width}x${resize.height}` : '600x800'
                }}
                ariaLabel="Book mockup image - drag and drop to upload new image">
                <BookMediaImgMockup
                  src={bookMockup}
                  alt="Book mockup preview showing cover design"
                  width={resize.width || 500}
                  height={resize.height || 500}
                  priority
                />
              </ConditionalImageStyled>
            </ImgInner>
          </StyledWrapper>

          <BackgroundImage
            role="img"
            aria-label="Hero background image"
            {...(isDashboard && {
              accessKey: backgroundAccessKey,
              title: `Press Alt+${backgroundAccessKey} to focus the background image area`,
              tabIndex: 0
            })}>
            <ConditionalImage
              backgObjPosition={updateImageMeta}
              isHeroBackground
              isLeftOrientation
              isSalePage
              css={imageStyles as never}
              disabled={!editable}
              onUpload={(file) => handleUpload(file, 'image_2')}
              loading={isLoading[1]}
              alreadyUploaded={Boolean(bgImage2)}
              imageFile={bgImage2}
              meta={meta}
              imageAspectRatio={{
                ratio: '16x9',
                pixels: '1920x1080'
              }}
              ariaLabel="Full-screen background image - drag and drop to upload new image">
              <Overlay
                className="overlay_hero-img"
                style={{
                  opacity: opacityBg,
                  backgroundColor: resolvedBackgroundColor
                }}
                aria-hidden="true"
                role="presentation"
              />

              <OverlayBlur
                className="overlay_hero-img_blur"
                style={{ backdropFilter: blurBg }}
                aria-hidden="true"
                role="presentation"
              />
              <BookMediaImg
                objposition={objPosition}
                src={bgImage2}
                width={500}
                height={500}
                alt=""
                aria-hidden="true"
                role="presentation"
                priority
              />
            </ConditionalImage>
          </BackgroundImage>
        </Banner>
        <Benefits>
          <StyledWrapperBenefits>
            <BenefitsInner role="region" aria-label="Key benefits and features">
              {benefits.map(({ content, icon, title }, index) => {
                const isTitleBenEmpty = checkTitleBenefits(index);
                const isContentBenEmpty = checkContentBenefits(index);
                const benefitAccessKey = generateAccessKey('hben', index);

                return (
                  <React.Fragment key={`${icon?.toString()}-${index}`}>
                    <BenefitItem
                      // key={`${icon?.toString()}-${index}`}
                      role="article"
                      aria-label={`Benefit ${index + 1}: ${title?.val || 'Key feature'}`}
                      {...(!isDashboard && {
                        accessKey: benefitAccessKey,
                        title: `Press Alt+${benefitAccessKey} to focus benefit ${index + 1}`,
                        tabIndex: 0
                      })}>
                      <StyledSvgIcons
                        iconName={`icon_${index + 1}` as 'icon_1' | 'icon_2' | 'icon_3'}
                        backgroundColor={button?.color}
                        aria-hidden="true"
                      />
                      <BenefitTxtInner>
                        <TitleBenefit
                          id={title?.id}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeRichText(title?.val, editable)
                          }}
                          style={{
                            color: filterBackgroundColor(sublineColor || '#fff', textColor)
                          }}
                          isEmpty={isTitleBenEmpty}
                          {...(isDashboard
                            ? {
                                onClick: (e: React.MouseEvent<HTMLElement>) => {
                                  try {
                                    updateActiveElement(e);
                                  } catch (error) {
                                    handleEditError(error);
                                  }
                                },
                                onBlur: resetActiveElement,
                                'data-accessor-parent': meta['accessorKey'],
                                'data-accessorkey': 'title',
                                'data-benefits': true,
                                'data-placeholder': '+',
                                editable: true
                              }
                            : {
                                'aria-label': !title?.val?.trim()
                                  ? `Benefit ${index + 1} title`
                                  : undefined
                              })}
                        />
                        <TextBenefit
                          id={content?.id}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeRichText(content?.val, editable)
                          }}
                          style={{
                            color: filterBackgroundColor(sublineColor || '#fff', textColor)
                          }}
                          isEmpty={isContentBenEmpty}
                          {...(isDashboard
                            ? {
                                onClick: (e: React.MouseEvent<HTMLElement>) => {
                                  try {
                                    updateActiveElement(e);
                                  } catch (error) {
                                    handleEditError(error);
                                  }
                                },
                                onBlur: resetActiveElement,
                                'data-accessor-parent': meta['accessorKey'],
                                'data-accessorkey': 'content',
                                'data-benefits': true,
                                'data-placeholder': '+',
                                editable: true
                              }
                            : {
                                'aria-label': !content?.val?.trim()
                                  ? `Benefit ${index + 1} description`
                                  : undefined,
                                'aria-describedby': title?.id
                              })}
                        />
                      </BenefitTxtInner>
                    </BenefitItem>
                    <BenefitBorder
                      style={{
                        backgroundColor: filterBackgroundColor(sublineColor || '#fff', textColor)
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </BenefitsInner>
          </StyledWrapperBenefits>
        </Benefits>
      </div>
    </HeroDetailed>
  );
};

V9.displayName = 'HeroImagesV6';
